import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import QuestionPreview from '../index';
import QuestionPreviewContainer from '../QuestionPreviewContainer';
import useHover from './useHover';
import useAppHelpers from './useAppHelpers';
import {
  showLoading,
  showPlayer,
  hideFirstPlayerItem,
  hideQuestionEl,
} from '../utils';
import useFeatures from './useFeatures';
import uuidToken from '../uuidToken';
import useMathJaxReplace from 'chameleon/ui-stack/hooks/useMathJaxReplace';

const useQuestionPreview = ({
  isAvailable,
  items,
  dynamicItems = [],
  hoveredItemParent,
  questionsContainer = 'table',
  containerOffsetX = 0,
  containerOffsetY = 0,
  containerPlacement = 'auto',
  shouldResetApp = true,
  qbCachedLrnQuestionsLimit,
  uuid = uuidToken(),
}) => {
  const containerRef = useRef();
  const [hoverProps, setHoverProps] = useState({});
  const [preview, setPreview] = useState(null);
  const [app, setApp] = useState(null);
  const loadingIndicatorRef = useRef();
  const cbPlayerRef = useRef();
  /**
   * Delay in MS.
   * How long we agreed is not too long for a user to wait until
   * they see the hover preview.
   */
  const ORIGINAL_HOVER_PREVIEW_DELAY = 200;
  /**
   * A timeout ID.
   * The timeout ID we should clear if the user doesn't hover
   * long enough to see the preview
   */
  const timeoutRef = useRef();
  /**
   * Delay in MS.
   * After the teacher looks at a hover preview, we reduce the hover
   * delay time to this value
   */
  const SMALLER_HOVER_DELAY = 0;
  /**
   * How long the user will wait until we start processing the hover preview
   */
  const hoverDelay = useRef(ORIGINAL_HOVER_PREVIEW_DELAY);
  const clearTimeoutRef = () => {
    clearTimeout(timeoutRef.current);
  };
  const restoreLongDelay = () => {
    hoverDelay.current = ORIGINAL_HOVER_PREVIEW_DELAY;
  };

  const { stopAudioFeatures, setFeatures } = useFeatures();

  const showPreview = (parent, reference, container, currentTarget) => {
    if (parent) {
      parent.dataset.reference = reference;
      parent.addEventListener('mouseleave', onMouseLeave, {
        once: true,
      });
      parent.addEventListener('mousedown', triggerMouseLeave, {
        once: true,
      });
      parent.addEventListener('click', triggerMouseLeave, {
        once: true,
      });
    }

    //safety measure
    if (container?.dataset?.activequestion) {
      hideQuestionEl(container, container?.dataset?.activequestion, uuid);
    }

    container.dataset.activequestion = reference;

    showPlayer(container);
    showLoading(container, loadingIndicatorRef.current, cbPlayerRef.current);
    tryComputingPosition(currentTarget, container);
    tryDisplayingPlayerQuestion({
      reference,
      container,
      loadingIndicatorRef,
      cbPlayerRef,
    });
    hoverDelay.current = SMALLER_HOVER_DELAY;
  };

  const [{ tryComputingPosition, onBlur, onMouseLeave, triggerMouseLeave }] =
    useHover({
      hoveredItemParent,
      getContainer: () => containerRef.current,
      containerOffsetX,
      containerOffsetY,
      containerPlacement,
      stopAudioFeatures,
      loadingIndicatorRef,
      cbPlayerRef,
      uuid,
      onMouseLeaveCallback: clearTimeoutRef,
    });
  const [
    {
      setDynamicItems,
      setHelperProps,
      tryDisplayingPlayerQuestion,
      setHelperItemIsLoading,
    },
  ] = useAppHelpers({
    setFeatures,
    qbCachedLrnQuestionsLimit,
    uuid,
  });

  useMathJaxReplace();

  useEffect(() => {
    if (app) {
      loadingIndicatorRef.current =
        containerRef.current.querySelector('.loading-indicator');
      cbPlayerRef.current = containerRef.current.querySelector(
        '.cb-learnosity-player',
      );
      setHelperProps(
        app,
        items,
        containerRef.current,
        loadingIndicatorRef.current,
        cbPlayerRef.current,
      );
    } else {
      setHelperItemIsLoading(get(items || [], '0.learnosity_item_reference'));
    }

    return () => {
      try {
        if (app && app?.reset && shouldResetApp) {
          app?.reset();
        }
      } catch (error) {
        // console.log('error', error);
      }
    };
  }, [app]);

  useEffect(() => {
    if (isAvailable && containerRef.current) {
      const firstQuestion = (items || [])[0];
      if (firstQuestion) {
        const { item_id, id } = firstQuestion;
        const questionId = item_id || id;
        setPreview(
          <QuestionPreview
            questionId={questionId}
            onLoadingEnd={(_app) => {
              hideFirstPlayerItem(
                containerRef.current,
                _app.getCurrentItem().reference,
                uuid,
              );
              setApp(_app);
            }}
            items={items || []}
          />,
        );

        const onMouseOverCallBack = (currentTarget) => {
          const { reference } = currentTarget.dataset;

          const container = containerRef.current;

          if (!container) {
            return;
          }

          const parent = currentTarget.closest(hoveredItemParent);
          /**
           * In most cases, this is a table hence the default prop value
           */
          const overallQuestionContainer =
            currentTarget.closest(questionsContainer);

          /**
           * Ensure we don't show the preview if we leave the question title
           * before we have been hovering for >= hoverDelay.current ms.
           *
           * If the preview is already open, it's still ok to clear the timeout.
           *
           * Ensure we don't have duplicate event listeners set on the element
           */
          currentTarget.removeEventListener('mouseleave', clearTimeoutRef);
          currentTarget.addEventListener('mouseleave', clearTimeoutRef);
          /**
           * Ensure we restore a long delay if the teacher leaves the table for any reason
           * i.e. navigates elsewhere, interacts with left nav, etc.
           *
           * Ensure we don't have duplicate event listeners set on the table
           */
          if (overallQuestionContainer) {
            overallQuestionContainer.removeEventListener(
              'mouseleave',
              restoreLongDelay,
            );
            overallQuestionContainer.addEventListener(
              'mouseleave',
              restoreLongDelay,
            );
          }
          /**
           * Show the user the hover preview after the current hoverDelay value, ensure to always cancel the previous timout
           */
          clearTimeoutRef();
          timeoutRef.current = setTimeout(
            () => showPreview(parent, reference, container, currentTarget),
            hoverDelay.current,
          );
        };

        setHoverProps({
          //enable accessibility
          onFocus: (e) => {
            const { currentTarget } = e;
            Promise.resolve().then(() => {
              onMouseOverCallBack(currentTarget);
            });
          },
          onBlur: (e) => {
            onBlur(e);
          },
          //disable focus on click
          onMouseDown: (e) => {
            e.preventDefault();
          },
          onMouseOver: (e) => {
            const { currentTarget } = e;
            Promise.resolve().then(() => {
              onMouseOverCallBack(currentTarget);
            });
          },
        });
      }
    }
  }, [isAvailable, containerRef.current]);

  useEffect(() => {
    if (app && (dynamicItems || []).length) {
      setDynamicItems(dynamicItems);
    }
  }, [app, dynamicItems]);

  const previewContainer = (
    <QuestionPreviewContainer
      containerRef={containerRef}
      loadingIndicatorRef={loadingIndicatorRef}
      cbPlayerRef={cbPlayerRef}
      stopAudioFeatures={stopAudioFeatures}
      uuid={uuid}
    />
  );

  return [
    {
      hoverProps,
      preview,
      previewContainer,
      app,
    },
  ];
};

useQuestionPreview.propTypes = {
  items: PropTypes.array.isRequired,
  dynamicItems: PropTypes.array,
  isAvailable: PropTypes.bool.isRequired,
  contentRef: PropTypes.object.isRequired,
  containerOffsetX: PropTypes.number,
  containerOffsetY: PropTypes.number,
  containerPlacement: PropTypes.string,
  shouldResetApp: PropTypes.bool,
};

export default useQuestionPreview;
